import React, { useEffect, useState, Fragment } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import RegistrationDetail from './RegistrationDetail';
import { formatInTimeZone } from 'date-fns-tz';

// Fixed timezone for consistent display across all users (US Central Time)
const DISPLAY_TIMEZONE = 'America/Chicago';


interface Registration {
    id: string;
    // 关联信息
    program?: EntityReference;
    programTitle?: string;
    courses?: EntityReference[];
    courseNames?: string[];
    courseAvatar?: string;
    user?: EntityReference;

    // 学生信息
    firstName?: string;
    lastName?: string;
    chineseName?: string;
    gender?: string;
    dateOfBirth?: Timestamp;
    grade?: string;
    allergies?: string;

    // 家长/监护人信息
    parentFirstName?: string;
    parentLastName?: string;
    phone?: string;
    email?: string;
    address?: string;
    aptNumber?: string;
    city?: string;
    state?: string;
    zipCode?: string;

    // 紧急联系人
    emergencyFirstName?: string;
    emergencyLastName?: string;
    emergencyPhone?: string;
    emergencyEmail?: string;
    emergencyAddress?: string;
    emergencyAptNumber?: string;
    emergencyCity?: string;
    emergencyState?: string;
    emergencyZipCode?: string;
    parentMessage?: string;

    // 状态信息
    status: string;
    createdAt: Timestamp;
    updatedAt?: Timestamp;

    // 价格信息
    totalPrice: number;
    earlyBirdPrice: number;
    amountPaid: number;
    // 收据信息
    receipt?: string;

    // 特定前端显示需要的字段
    selectedDates?: Timestamp[];
}

// 添加EntityReference接口，因为从Firebase导入
interface EntityReference {
    id: string;
    path: string;
}

const UserRegistrations: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchRegistrations = async () => {
            if (!auth.currentUser) return;
            
            try {
                setLoading(true);
                setError(null);
                
                const registrationsQuery = query(
                    collection(db, 'registrations'),
                    where('user', '==', doc(db, 'users', auth.currentUser.uid)),
                    orderBy('createdAt', 'desc')
                );
                
                const querySnapshot = await getDocs(registrationsQuery);
                
                if (querySnapshot.empty) {
                    setRegistrations([]);
                    setLoading(false);
                    return;
                }
                
                // 获取所有注册记录数据
                const registrationsData = querySnapshot.docs.map(docSnapshot => {
                    const data = docSnapshot.data();
                    return {
                        id: docSnapshot.id,
                        program: data.program,
                        programTitle: '', // 暂时为空，后面批量获取
                        courses: data.courses || [],
                        status: data.status || 'pending',
                        receipt: data.receipt || null,
                        createdAt: data.createdAt,
                        updatedAt: data.updatedAt,
                        totalPrice: data.totalPrice || 0,
                        amountPaid: data.amountPaid || 0,
                        earlyBirdPrice: data.earlyBirdPrice || data.totalPrice,
                        selectedDates: data.selectedDates || [],

                        // 学生信息
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        chineseName: data.chineseName || '',
                        gender: data.gender || '',
                        dateOfBirth: data.dateOfBirth,
                        grade: data.grade || '',
                        allergies: data.allergies || '',

                        // 家长信息
                        parentFirstName: data.parentFirstName || '',
                        parentLastName: data.parentLastName || '',
                        phone: data.phone || '',
                        email: data.email || '',
                        address: data.address || '',
                        aptNumber: data.aptNumber || '',
                        city: data.city || '',
                        state: data.state || '',
                        zipCode: data.zipCode || '',

                        // 紧急联系人信息
                        emergencyFirstName: data.emergencyFirstName || '',
                        emergencyLastName: data.emergencyLastName || '',
                        emergencyPhone: data.emergencyPhone || '',
                        emergencyEmail: data.emergencyEmail || '',
                        emergencyAddress: data.emergencyAddress || '',
                        emergencyAptNumber: data.emergencyAptNumber || '',
                        emergencyCity: data.emergencyCity || '',
                        emergencyState: data.emergencyState || '',
                        emergencyZipCode: data.emergencyZipCode || '',

                        // 附加消息
                        parentMessage: data.parentMessage || ''
                    };
                });
                
                // 收集所有项目引用进行批量获取
                const programRefs = registrationsData
                    .filter(reg => reg.program)
                    .map(reg => reg.program as EntityReference);

                // 使用Set去重，避免重复获取相同项目
                const uniqueProgramRefs = Array.from(
                    new Set(programRefs.map(ref => (ref.path || 'programs') + '/' + ref.id))
                ).map(pathId => {
                    const [path, id] = pathId.split('/');
                    return { path, id } as EntityReference;
                });

                // 批量获取项目信息
                const programsData: Record<string, any> = {};

                if (uniqueProgramRefs.length > 0) {
                    const programDocs = await Promise.all(
                        uniqueProgramRefs.map(ref => {
                            // 确保路径格式正确
                            const collectionId = ref.path || 'programs';
                            return getDoc(doc(db, collectionId, ref.id));
                        })
                    );

                    programDocs.forEach((docSnap, index) => {
                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            const refKey = `${uniqueProgramRefs[index].id}`;

                            // 根据字段命名规则保存项目数据
                            programsData[refKey] = {
                                title: data.title || data.name || t('registration.unknownProgram'),
                                subtitle: data.subtitle || data.nameEn || '', // 英文标题/副标题
                            };
                        }
                    });
                }
                // 将项目信息填充到注册记录中
                const enrichedRegistrations = registrationsData.map(reg => {
                    if (reg.program) {
                        const refKey = `${reg.program.id}`;
                        const programData = programsData[refKey];
                        if (programData) {
                            // 根据当前语言选择标题
                            const currentLanguage = i18n.language === 'zh' ? 'zh' : 'en';
                            if (currentLanguage === 'zh') {
                                reg.programTitle = programData.title || t('registration.unknownProgram');
                            } else {
                                reg.programTitle = programData.subtitle || programData.title || t('registration.unknownProgram');
                            }
                        } else {
                            reg.programTitle = t('registration.unknownProgram');
                        }
                    } else {
                        reg.programTitle = t('registration.unknownProgram');
                    }
                    return reg;
                });

                setRegistrations(enrichedRegistrations);
            } catch (err) {
                console.error('获取报名记录失败:', err);
                setError(t('profile.registrationsFetchError'));
            } finally {
                setLoading(false);
            }
        };
        
        fetchRegistrations();
    }, [t]);
    
    const handleRegistrationClick = (registration: Registration) => {
        setSelectedRegistration(registration);
        setShowModal(true);
    };
    
    const closeModal = () => {
        setShowModal(false);
        setSelectedRegistration(null);
    };
    
    if (loading) {
        return (
            <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-center py-8">
                    <p className="text-red-300">{error}</p>
                </div>
            </div>
        );
    }
    
    if (registrations.length === 0) {
        return (
            <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-center py-8">
                    <p className="text-blue-100">{t('profile.noRegistrations')}</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-4">{t('profile.myRegistrations')}</h2>
            
            <UserRegistrationList
                registrations={registrations}
                onRegistrationClick={handleRegistrationClick}
            />

            {selectedRegistration && (
                <RegistrationDetail
                    registration={selectedRegistration}
                    isOpen={showModal}
                    onClose={closeModal}
                />
            )}
        </div>
    );
};

// 注册列表组件
const UserRegistrationList: React.FC<{
    registrations: Registration[];
    onRegistrationClick: (registration: Registration) => void;
}> = ({ registrations, onRegistrationClick }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const totalPages = Math.ceil(registrations.length / itemsPerPage);
    const paginatedRegistrations = registrations.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const formatDate = (timestamp: Timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate();
        // Use consistent timezone for display to avoid timezone conversion issues
        return formatInTimeZone(date, DISPLAY_TIMEZONE, 'M/d/yyyy');
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'approved':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'waitlisted':
                return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'cancelled':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            case 'pending':
            default:
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        }
    };

    return (
        <>
            <div className="space-y-4">
                {paginatedRegistrations.map((registration, index) => (
                    <motion.div 
                        key={registration.id}
                        className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:bg-white/15 transition-colors cursor-pointer"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        onClick={() => onRegistrationClick(registration)}
                    >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                            <div className="mb-2 md:mb-0">
                                <div className="flex items-center mb-1">
                                    <h3 className="text-lg font-medium text-white mr-3">{registration.programTitle}</h3>
                                    {/* <PriceBadge price={registration.totalPrice} /> */}
                                    {registration.courses && registration.courses.length > 0 && (
                                        <CourseCountBadge count={registration.courses.length} />
                                    )}
                                </div>
                                <p className="text-blue-200">{registration.createdAt ? formatDate(registration.createdAt) : '-'}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className={`px-2 py-1 text-xs rounded-full border ${getStatusBadgeClass(registration.status)}`}>
                                    {t(`profile.registrationStatus.${registration.status}`)}
                                </span>
                                {registration.receipt && (
                                    <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-full flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {t('profile.receipt')}
                                </span>
                                )}
                            </div>
                        </div>
                        
                        {registration.selectedDates && registration.selectedDates.length > 0 && (
                            <div className="mt-3">
                                <p className="text-sm text-blue-100 mb-1">{t('profile.selectedDates')}:</p>
                                <div className="flex flex-wrap gap-2">
                                    {registration.selectedDates.slice(0, 3).map((date, i) => (
                                        <span key={i} className="px-2 py-1 text-xs bg-blue-900/50 text-blue-100 rounded border border-blue-700">
                                            {formatDate(date)}
                                        </span>
                                    ))}
                                    {registration.selectedDates.length > 3 && (
                                        <span className="px-2 py-1 text-xs bg-blue-900/50 text-blue-100 rounded border border-blue-700">
                                            +{registration.selectedDates.length - 3} {t('profile.moreDates')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>
            
            {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                    <nav className="flex items-center space-x-2">
                        <button
                            onClick={() => goToPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded-md ${currentPage === 1
                                    ? 'bg-blue-900/30 text-blue-300/50 cursor-not-allowed'
                                    : 'bg-blue-900/50 text-blue-100 hover:bg-blue-800/70'
                            }`}
                        >
                            &laquo;
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => goToPage(page)}
                                className={`px-3 py-1 rounded-md ${currentPage === page
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-blue-900/50 text-blue-100 hover:bg-blue-800/70'
                                }`}
                            >
                                {page}
                            </button>
                        ))}
                        
                        <button
                            onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded-md ${currentPage === totalPages
                                    ? 'bg-blue-900/30 text-blue-300/50 cursor-not-allowed'
                                    : 'bg-blue-900/50 text-blue-100 hover:bg-blue-800/70'
                            }`}
                        >
                            &raquo;
                        </button>
                    </nav>
                </div>
            )}
        </>
    );
};

// 在UserRegistrationList组件中添加一个价格徽章组件
const PriceBadge: React.FC<{ price: number }> = ({ price }) => {
    return (
        <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white px-3 py-1 rounded-full shadow-md text-sm font-medium flex items-center">
            <span className="text-white/80 mr-1">$</span>{price}
                                            </div>
    );
};

// 在UserRegistrationList组件中添加一个课程徽章组件
const CourseCountBadge: React.FC<{ count: number }> = ({ count }) => {
    const { t } = useTranslation();

    if (count <= 0) return null;

    return (
        <div className="ml-2 px-2 py-0.5 bg-blue-600/50 text-blue-100 rounded-full text-xs flex items-center">
            <span className="mr-1">{count}</span>
            <span>{count > 1 ? t('profile.coursesLabel') : t('profile.courseLabel')}</span>
        </div>
    );
};

export default UserRegistrations; 